import React from 'react';
import { Shield, Lock, EyeOff, FileText, Server, Globe, ArrowRight } from 'lucide-react';

const PrivacyPolicyPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-950">
      {/* Hero Section */}
      <section className="relative w-full py-20 flex items-center overflow-hidden">
        {/* Fondos */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-950 via-gray-900 to-black" />
        <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1604933762021-54a5858c9832?ixlib=rb-4.0.3')] bg-cover bg-center opacity-5" />
        
        {/* Efecto de resplandor */}
        <div className="absolute inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-purple-800/20 rounded-full blur-[100px] animate-pulse" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] bg-blue-800/20 rounded-full blur-[80px] animate-pulse delay-300" />
        </div>
        
        {/* Contenido */}
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6 relative">
          <div className="text-center">
            <div className="inline-flex items-center px-4 py-2 bg-purple-900/20 backdrop-blur-sm rounded-full text-purple-400 mb-6 border border-purple-900/30">
              <Shield className="w-4 h-4 mr-2" />
              <span className="text-sm font-medium">Documento Legal</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              Política de{' '}
              <span className="relative">
                <span className="relative z-10 text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-300">
                  Privacidad
                </span>
                <span className="absolute inset-0 bg-gradient-to-r from-purple-400/20 to-blue-300/20 blur-lg" />
              </span>
            </h1>
            
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
              Tu privacidad es importante para nosotros. Conoce cómo recopilamos, usamos y protegemos tus datos personales.
            </p>
            
            <div className="flex flex-wrap justify-center gap-8 mb-12">
              <div className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-lg backdrop-blur-sm border border-white/10">
                <Lock className="w-5 h-5 text-purple-400" />
                <span className="text-gray-300">Actualizada: {new Date().toLocaleDateString()}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contenido de la política */}
      <section className="py-16 relative overflow-hidden bg-gradient-to-br from-gray-950 via-gray-900/50 to-purple-950/30">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/3 w-[400px] h-[400px] bg-purple-900/15 rounded-full blur-[100px]" />
          <div className="absolute bottom-1/3 right-1/4 w-[300px] h-[300px] bg-blue-900/15 rounded-full blur-[80px]" />
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="bg-gradient-to-br from-gray-900/80 to-purple-950/50 p-8 md:p-12 rounded-xl backdrop-blur-sm border border-purple-500/20 shadow-lg">
            <div className="space-y-12">
              {/* Introducción */}
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">1. Introducción</h2>
                <p className="text-gray-300 mb-4">
                  En Curvia AI ("nosotros", "nuestro", "la empresa"), respetamos tu privacidad y nos comprometemos a proteger tus datos personales. Esta política de privacidad te informará sobre cómo cuidamos tus datos personales cuando visitas nuestro sitio web y te dirá acerca de tus derechos de privacidad y cómo la ley te protege.
                </p>
                <p className="text-gray-300">
                  Esta política de privacidad se aplica a toda la información recopilada a través de nuestro sitio web, así como cualquier servicio, venta, marketing o eventos relacionados.
                </p>
              </div>
              
              {/* Información que recopilamos */}
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">2. Información que recopilamos</h2>
                <p className="text-gray-300 mb-4">
                  Recopilamos varios tipos de información para proporcionar y mejorar nuestro servicio:
                </p>
                
                <div className="mt-6 space-y-4">
                  <div className="flex items-start gap-4 bg-white/5 p-4 rounded-lg">
                    <div className="mt-1 w-10 h-10 bg-purple-900/50 rounded-full flex items-center justify-center text-purple-400 flex-shrink-0">
                      <FileText className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-1">Datos personales</h3>
                      <p className="text-gray-300 text-sm">Nombre, dirección de correo electrónico, número de teléfono, dirección postal, y otra información similar que puedas proporcionarnos.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4 bg-white/5 p-4 rounded-lg">
                    <div className="mt-1 w-10 h-10 bg-purple-900/50 rounded-full flex items-center justify-center text-purple-400 flex-shrink-0">
                      <Server className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-1">Datos de uso</h3>
                      <p className="text-gray-300 text-sm">Información sobre cómo utilizas nuestro sitio web, servicios y productos, incluyendo el tiempo de uso, las páginas visitadas y las funciones utilizadas.</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4 bg-white/5 p-4 rounded-lg">
                    <div className="mt-1 w-10 h-10 bg-purple-900/50 rounded-full flex items-center justify-center text-purple-400 flex-shrink-0">
                      <Globe className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-1">Datos técnicos</h3>
                      <p className="text-gray-300 text-sm">Dirección IP, tipo y versión del navegador, configuración de la zona horaria, tipos y versiones de plugins, sistema operativo y plataforma, y otra tecnología en los dispositivos que utilizas para acceder a nuestro sitio web.</p>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Cómo utilizamos tus datos */}
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">3. Cómo utilizamos tus datos</h2>
                <p className="text-gray-300 mb-6">
                  Usamos la información recopilada de diversas formas, incluyendo:
                </p>
                
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300">Proporcionar, operar y mantener nuestro sitio web y servicios</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300">Mejorar, personalizar y expandir nuestro sitio web y servicios</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300">Entender y analizar cómo utilizas nuestro sitio web y servicios</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300">Desarrollar nuevos productos, servicios, características y funcionalidades</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300">Comunicarnos contigo por correo electrónico, o por otros medios, para proporcionar actualizaciones, noticias y otra información relacionada con el sitio web</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300">Procesar tus transacciones</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300">Enviar correos electrónicos que has solicitado, como boletines o noticias sobre productos</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300">Encontrar y prevenir el fraude</span>
                  </li>
                </ul>
              </div>
              
              {/* Compartir tus datos */}
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">4. Compartir tus datos personales</h2>
                <p className="text-gray-300 mb-4">
                  No vendemos, alquilamos ni compartimos tus datos personales con terceros sin tu consentimiento, excepto en las siguientes circunstancias:
                </p>
                
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300">Cuando sea necesario para proveedores de servicios que nos ayudan a operar nuestro sitio web y a brindar servicios (procesadores de pagos, proveedores de alojamiento web, etc.)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300">Para cumplir con obligaciones legales, como responder a citaciones o órdenes judiciales</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300">Para proteger nuestros derechos, propiedad o seguridad, o los de nuestros usuarios</span>
                  </li>
                </ul>
              </div>
              
              {/* Seguridad de datos */}
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">5. Seguridad de datos</h2>
                <p className="text-gray-300 mb-4">
                  Hemos implementado medidas de seguridad apropiadas para evitar que tus datos personales se pierdan, usen o accedan de manera no autorizada, se modifiquen o divulguen. Además, limitamos el acceso a tus datos personales a aquellos empleados, agentes, contratistas y otros terceros que tienen una necesidad comercial de conocer.
                </p>
                <p className="text-gray-300">
                  Todos nuestros proveedores de servicios externos están obligados a tomar medidas de seguridad adecuadas para proteger tus datos personales de acuerdo con nuestras políticas.
                </p>
              </div>
              
              {/* Tus derechos */}
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">6. Tus derechos</h2>
                <p className="text-gray-300 mb-4">
                  Dependiendo de tu ubicación, puedes tener ciertos derechos con respecto a tus datos personales:
                </p>
                
                <ul className="space-y-3">
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300">Derecho a acceder, actualizar o eliminar la información que tenemos sobre ti</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300">Derecho de rectificación: derecho a corregir la información que tenemos sobre ti</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300">Derecho de oposición: derecho a oponerte al procesamiento de tus datos</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <ArrowRight className="w-5 h-5 text-purple-400 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300">Derecho a la portabilidad de datos: derecho a solicitar una copia de tus datos personales en formato electrónico</span>
                  </li>
                </ul>
              </div>
              
              {/* Contacto */}
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">7. Cómo contactarnos</h2>
                <p className="text-gray-300">
                  Si tienes preguntas sobre esta política de privacidad o deseas ejercer tus derechos, puedes contactarnos en:
                </p>
                
                <div className="mt-6 bg-white/5 p-6 rounded-lg border border-purple-500/20">
                  <p className="text-white mb-2">Email: privacy@curviaai.com</p>
                  <p className="text-white mb-2">Teléfono: +34 91 123 45 67</p>
                  <p className="text-white">Dirección: Madrid, España</p>
                </div>
              </div>
              
              {/* Cambios en la política */}
              <div>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">8. Cambios en esta política</h2>
                <p className="text-gray-300 mb-4">
                  Podemos actualizar nuestra política de privacidad de vez en cuando. Te notificaremos cualquier cambio publicando la nueva política de privacidad en esta página y, si los cambios son significativos, te enviaremos un correo electrónico.
                </p>
                <p className="text-gray-300">
                  Te recomendamos revisar esta política de privacidad periódicamente para estar informado de cómo protegemos tu información.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicyPage; 